import type { Eticheta, EfsaIngredient, IngredientItem, Nivel } from './types'

export const CUVINTE_INTERZISE = [
  'trateaza',
  'tratează',
  'vindeca',
  'vindecă',
  'previne boala',
  'elimina boala',
  'elimină boala',
  'terapeutic',
  'medicament',
  'leac',
  'cure',
  'treat',
  'heal',
]

export const BOTANICE_KEYWORDS = ['ashwagandha', 'berberina', 'withania', 'berberis']

export interface ValidationResult {
  issues: string[]
  warnings: string[]
}

export function valideazaEticheta(e: Partial<Eticheta>): ValidationResult {
  const issues: string[] = []
  const warnings: string[] = []

  // Issues blocante
  if (!e.denumire?.toUpperCase().includes('PREMIUM')) {
    issues.push('Denumirea trebuie să conțină „PREMIUM”')
  }
  if (!e.mentiune || e.mentiune.trim().length < 15) {
    issues.push('Mențiunea EFSA principală lipsește sau are sub 15 caractere')
  }
  if (e.mentiune && containsClaimMedical(e.mentiune)) {
    issues.push('Mențiunea conține cuvinte interzise (afirmație medicală)')
  }
  if (!e.notificare || e.notificare.trim().length < 8) {
    issues.push('Nr. notificare lipsește sau are sub 8 caractere')
  }
  if (!e.ean || !/^\d{13}$/.test(e.ean)) {
    issues.push('EAN-ul trebuie să aibă exact 13 cifre')
  }
  for (const b of e.beneficii ?? []) {
    if (b.status === 'rosu') {
      issues.push(`Beneficiu cu afirmație interzisă: „${b.text.slice(0, 40)}…”`)
    }
  }

  // Warnings
  if (e.ean && /^\d{13}$/.test(e.ean) && !e.ean.startsWith('5944759')) {
    warnings.push('EAN-ul nu începe cu prefixul B4L standard (5944759)')
  }
  const utilizareCombo = `${e.utilizareAdulti ?? ''} ${e.utilizareCopii ?? ''}`.toLowerCase()
  if (utilizareCombo && !utilizareCombo.includes('recomandarea specialistului')) {
    warnings.push('Modul de utilizare nu conține fraza „sau la recomandarea specialistului”')
  }
  if (e.forma === 'Capsule moi' && (e.pictograme ?? []).includes('VEGAN')) {
    warnings.push('Capsulele moi sunt vegetariene, nu vegane. Schimbă pictograma.')
  }
  const pict = e.pictograme ?? []
  if (!pict.includes('FARA_GLUTEN')) {
    warnings.push('Lipsește pictograma FĂRĂ GLUTEN')
  }
  if (!pict.includes('FARA_LACTOZA')) {
    warnings.push('Lipsește pictograma FĂRĂ LACTOZĂ')
  }

  return { issues, warnings }
}

export function containsClaimMedical(text: string): boolean {
  const lower = text.toLowerCase()
  return CUVINTE_INTERZISE.some((c) => lower.includes(c))
}

export function validateBeneficiu(
  text: string,
  ingrediente: IngredientItem[],
): 'verde' | 'portocaliu' | 'rosu' | 'gol' {
  if (!text.trim()) return 'gol'
  if (containsClaimMedical(text)) return 'rosu'
  const lower = text.toLowerCase()
  if (BOTANICE_KEYWORDS.some((k) => lower.includes(k))) return 'portocaliu'
  // Verifica daca textul mentioneaza vreun ingredient adaugat
  for (const i of ingrediente) {
    const labelLower = i.label.toLowerCase()
    if (labelLower && lower.includes(labelLower.split(' ')[0])) {
      if (i.nivel === 'verde') return 'verde'
      if (i.nivel === 'portocaliu') return 'portocaliu'
      if (i.nivel === 'rosu') return 'rosu'
    }
  }
  return 'portocaliu'
}

export interface UlCheckResult {
  nivel: 'rosu' | 'portocaliu' | 'galben' | null
  pct: number
  mesaj: string
}

export function checkDozaVsUL(ingredient: EfsaIngredient, dozaText: string): UlCheckResult | null {
  if (!ingredient.ul || !dozaText.trim()) return null
  // Extract number from "100 mg", "0.5 g", "150"
  const m = dozaText.replace(',', '.').match(/(\d+(?:\.\d+)?)/)
  if (!m) return null
  let value = parseFloat(m[1])
  const ulValue = parseFloat(ingredient.ul)
  const ulUnit = ingredient.ulUnit ?? 'mg'

  // Detectare unitate doza vs UL
  const lowerText = dozaText.toLowerCase()
  let dozaUnit = 'mg'
  if (lowerText.includes('mcg') || lowerText.includes('μg') || lowerText.includes('ug'))
    dozaUnit = 'mcg'
  else if (/(\b\d+\.?\d*\s*g\b)/.test(lowerText) && !lowerText.includes('mg')) dozaUnit = 'g'

  // Conversie la unitatea UL
  if (dozaUnit === 'mcg' && ulUnit === 'mg') value = value / 1000
  else if (dozaUnit === 'mg' && ulUnit === 'mcg') value = value * 1000
  else if (dozaUnit === 'g' && ulUnit === 'mg') value = value * 1000
  else if (dozaUnit === 'g' && ulUnit === 'mcg') value = value * 1000000

  const pct = (value / ulValue) * 100

  if (pct >= 100) {
    return {
      nivel: 'rosu',
      pct,
      mesaj: `Doza depășește limita superioară tolerată (UL): ${value} ${ulUnit}, peste ${ulValue} ${ulUnit}/zi conform EFSA. Redu doza.`,
    }
  }
  if (pct >= 75) {
    return {
      nivel: 'portocaliu',
      pct,
      mesaj: `Aproape de UL (${pct.toFixed(0)}% din ${ulValue} ${ulUnit}/zi). Monitorizare recomandată.`,
    }
  }
  if (pct >= 50) {
    return {
      nivel: 'galben',
      pct,
      mesaj: `Doză moderată (${pct.toFixed(0)}% din UL). În limite normale.`,
    }
  }
  return null
}

export function nivelFromIngredient(i: EfsaIngredient): Nivel {
  return i.nivel
}
